import type { EditOperation } from '../chat/types';

/**
 * Word tracked-change helpers for the per-edit Accept/Reject UX.
 *
 * Office JS 1.6+ exposes `Word.TrackedChange` with `.accept()` / `.reject()`.
 * We filter by author string (`NyayAssist AI`) so user-made hand-edits
 * aren't touched, and correlate to a specific `EditOperation` by matching
 * the change's inserted text to `op.newText` (deletions match `op.oldText`).
 *
 * Perf notes:
 *   - Always load {author, type, text} in a single `context.sync()`.
 *   - Avoid calling `getTrackedChanges()` inside loops — fetch once, filter
 *     in JS. Word's proxy cost dominates otherwise.
 *   - Scroll-into-view uses `range.select()` then `context.sync()`; calling
 *     `scrollIntoView` on a proxied range has quirks across platforms.
 */

const AI_AUTHOR = 'NyayAssist AI';

/**
 * Office's TrackedChange.type at runtime is one of
 *   'Added' | 'Deleted' | 'Formatted' | 'None'
 * (the TypeScript typings also allow the enum, hence the widened union).
 */
type TrackedChangeKind =
  | Word.TrackedChangeType
  | 'Added'
  | 'Deleted'
  | 'Formatted'
  | 'None';

interface LoadedChange {
  tc: Word.TrackedChange;
  author: string;
  type: TrackedChangeKind;
  text: string;
}

function isAddition(t: TrackedChangeKind): boolean {
  const s = String(t).toLowerCase();
  return s === 'added';
}
function isDeletion(t: TrackedChangeKind): boolean {
  const s = String(t).toLowerCase();
  return s === 'deleted';
}

async function loadAiChanges(context: Word.RequestContext): Promise<LoadedChange[]> {
  const all = context.document.body.getTrackedChanges();
  all.load('items/author,items/type,items/text');
  await context.sync();
  const loaded: LoadedChange[] = [];
  for (const tc of all.items) {
    if (tc.author !== AI_AUTHOR) continue;
    loaded.push({ tc, author: tc.author, type: tc.type, text: tc.text });
  }
  return loaded;
}

/**
 * Find tracked changes that correspond to a given edit operation.
 *
 * Matching rules (loose but effective):
 *   - 'replace' → any change whose text is contained in oldText or newText
 *   - 'insert'  → inserted changes whose text matches newText
 *   - 'delete'  → deleted changes whose text matches oldText
 *
 * We normalise whitespace before comparing; Word can reflow whitespace
 * during OOXML insertion.
 */
function matches(op: EditOperation, ch: LoadedChange): boolean {
  const chText = norm(ch.text);
  const oldText = norm(op.oldText);
  const newText = norm(op.newText);
  if (!chText) return false;

  const ins = isAddition(ch.type);
  const del = isDeletion(ch.type);

  if (op.editType === 'insert') {
    return ins && (chText === newText || newText.includes(chText));
  }
  if (op.editType === 'delete') {
    return del && (chText === oldText || oldText.includes(chText));
  }
  // replace — either side may match
  if (ins) return chText === newText || newText.includes(chText);
  if (del) return chText === oldText || oldText.includes(chText);
  return false;
}

function norm(s: string | undefined): string {
  return (s ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Accept every tracked change authored by the AI that corresponds to this
 * operation. Returns the number of changes that were affected.
 */
export async function acceptOperation(op: EditOperation): Promise<number> {
  return Word.run(async (context) => {
    const matches_ = (await loadAiChanges(context)).filter((c) => matches(op, c));
    for (const m of matches_) m.tc.accept();
    await context.sync();
    return matches_.length;
  });
}

export async function rejectOperation(op: EditOperation): Promise<number> {
  return Word.run(async (context) => {
    const matches_ = (await loadAiChanges(context)).filter((c) => matches(op, c));
    for (const m of matches_) m.tc.reject();
    await context.sync();
    return matches_.length;
  });
}

/**
 * Bulk accept/reject — single sync, much cheaper than N round-trips.
 */
export async function acceptOperations(ops: EditOperation[]): Promise<number> {
  if (ops.length === 0) return 0;
  return Word.run(async (context) => {
    const loaded = await loadAiChanges(context);
    let count = 0;
    for (const op of ops) {
      for (const c of loaded) {
        if (matches(op, c)) {
          c.tc.accept();
          count += 1;
        }
      }
    }
    await context.sync();
    return count;
  });
}

export async function rejectOperations(ops: EditOperation[]): Promise<number> {
  if (ops.length === 0) return 0;
  return Word.run(async (context) => {
    const loaded = await loadAiChanges(context);
    let count = 0;
    for (const op of ops) {
      for (const c of loaded) {
        if (matches(op, c)) {
          c.tc.reject();
          count += 1;
        }
      }
    }
    await context.sync();
    return count;
  });
}

/**
 * Select + scroll the first tracked change matching this operation into view.
 * Returns false if no match (card stays, user sees no-op).
 */
export async function scrollToOperation(op: EditOperation): Promise<boolean> {
  return Word.run(async (context) => {
    const all = context.document.body.getTrackedChanges();
    all.load('items/author,items/type,items/text');
    await context.sync();
    for (const tc of all.items) {
      if (tc.author !== AI_AUTHOR) continue;
      if (matches(op, { tc, author: tc.author, type: tc.type, text: tc.text })) {
        const range = tc.getRange();
        range.select();
        await context.sync();
        return true;
      }
    }
    return false;
  });
}

/**
 * Refresh the accepted/rejected status for a batch of operations by
 * re-enumerating tracked changes. If an operation's tracked change no
 * longer exists in the document, we mark it as 'accepted' (Word accepts
 * changes by removing them from the list; text stays). If it still exists,
 * it's still 'pending'. Reject-then-check would return 'rejected' the
 * same way — but we can't distinguish accept-removed from reject-removed,
 * so this function only confirms presence (pending) vs absence. The
 * authoritative accepted/rejected state lives in `document.settings`.
 */
export async function listActiveAiChangeTexts(): Promise<string[]> {
  return Word.run(async (context) => {
    const loaded = await loadAiChanges(context);
    return loaded.map((c) => norm(c.text));
  });
}
